/*
  Mobile menu drawer.

  Deliberately independent of Dawn's MenuDrawer: that component binds a
  close handler to every button it contains and its summary handler only
  ever opens a submenu, so inline accordions can't work inside it. Dawn's
  component is left alone and still runs the mobile filter drawer.

  Behaviour: a slide-in panel with its own focus trap, Escape to close,
  scroll lock while open, and inline accordions for menu items that have
  children. Closing returns focus to the button that opened it.
*/
if (!customElements.get('koko-drawer')) {
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class KokoDrawer extends HTMLElement {
    connectedCallback() {
      this.openButton = this.querySelector('.koko-drawer__open');
      this.panel = this.querySelector('.koko-drawer__panel');
      if (!this.openButton || !this.panel) return;

      this.onKeydown = this.onKeydown.bind(this);

      // Toggle, so tapping the hamburger again closes it.
      this.openButton.addEventListener('click', () =>
        this.classList.contains('is-open') ? this.close() : this.open()
      );
      this.querySelectorAll('[data-koko-drawer-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );
      this.querySelectorAll('[data-koko-drawer-toggle]').forEach((button) =>
        button.addEventListener('click', () => this.toggleSection(button))
      );
      // Following a link should leave the drawer closed behind it, which
      // matters when the browser restores the page from the back/forward
      // cache with its markup exactly as it was left.
      this.panel.addEventListener('click', (event) => {
        if (event.target.closest('a')) this.close({ restoreFocus: false });
      });
    }

    open() {
      this.classList.add('is-open');
      this.openButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add('koko-drawer-open');
      document.addEventListener('keydown', this.onKeydown);
      // Wait a frame so the panel is visible before it takes focus.
      requestAnimationFrame(() => {
        const close = this.panel.querySelector('.koko-drawer__close');
        if (close) close.focus();
      });
    }

    close({ restoreFocus = true } = {}) {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.openButton.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('koko-drawer-open');
      document.removeEventListener('keydown', this.onKeydown);
      if (restoreFocus) this.openButton.focus();
    }

    toggleSection(button) {
      const submenu = document.getElementById(button.getAttribute('aria-controls'));
      if (!submenu) return;
      const isOpen = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!isOpen));
      submenu.hidden = isOpen;
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('koko-drawer', KokoDrawer);
}
